import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ControlContainer, NgForm, NgModel } from '@angular/forms';
import { FinancialCriteria } from './filter-financial.model';
import { FilterTypes, FinancialCriteriaOptions } from '@models/filterEnums.model';
import { Ratio } from '@models/ratio.model';
import { Study } from '@models/study.model';
import { VendorAccount } from '@models/vendoraccount.model';
import { FilterService } from '@services/filter.service';
import { LoggingService } from '@services/logging.service';
import { LookupService } from '@services/lookup.service';
import { SearchStrategyService } from '@services/search-strategy.service';
import * as Constants from '@shared/_constants';
import { RegularExpressionPattern } from '@shared/_constants';
import { TreeNode } from 'primeng/api';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterUtilService } from '../shared/filter-util.service';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { selectStudyData } from '../../study.selectors';
import { selectSearchStrategyDatabases } from '../../analysis/search-strategy/state/study-search-strategy.selectors';
import { StudySearchStrategyDatabase } from '../../../models/studySearchStrategyDatabase.model';
import _ from 'lodash-es';

@Component({
  selector: 'app-filter-financial',
  templateUrl: './filter-financial.component.html',
  styleUrls: ['./filter-financial.component.css'],
  providers: [SearchStrategyService],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  encapsulation: ViewEncapsulation.None,
})
export class FilterFinancialComponent implements OnInit {
  @Input() filter: any;
  @Input() readOnly = false;
  @Input() submitClicked: boolean;

  study: Study;
  searchStrategyDatabases: StudySearchStrategyDatabase[];
  regularExpressionPattern: typeof RegularExpressionPattern = RegularExpressionPattern;
  dataAvailabilityOption: string = FinancialCriteriaOptions.DataAvailability;
  financialAbsoluteYears: TreeNode[] = [];
  financialRatioOption: string = FinancialCriteriaOptions.FinancialRatio;
  financialItemOption: string = FinancialCriteriaOptions.FinancialItem;
  filterBefore = '';

  financialRatios: Ratio[];
  validCriteriaTotalYears: number[] = [];
  filterVendorAccounts: VendorAccount[];

  formDirty = false;
  currencyName = '';
  currencyUnitName = '';
  disablePassIfNa = false;
  isBalanceSheetValueSwitchAvailable = false;

  private destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private logger: LoggingService,
    private filterService: FilterService,
    private filterUtilService: FilterUtilService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectStudyData))
      .subscribe((study) => {
        this.study = study;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyDatabases))
      .subscribe((databases) => {
        this.searchStrategyDatabases = databases;
      });

    this.loadInitialData();
    this.setPassIfNaOption();
  }

  initializeForm() {
    if (this.filter instanceof FinancialCriteria) {
      if ((<FinancialCriteria>this.filter).selectedAbsoluteYr) {
        this.filter.selectedAbsoluteYr = this.flattenTree(this.financialAbsoluteYears).filter((selection) =>
          this.findNodeRecursive(selection.data, (<FinancialCriteria>this.filter).selectedAbsoluteYr),
        );
        this.sortSelection(this.filter.selectedAbsoluteYr);
        this.validCriteriaTotalYears = [];
        this.filter.selectedAbsoluteYr.forEach((ele, index) => {
          this.validCriteriaTotalYears.push(index + 1);
        });

        if (
          this.filter.hasAverageForSelectedYears &&
          (this.financialRatioOption === this.filter.criteriaSelection || this.financialItemOption === this.filter.criteriaSelection)
        ) {
          if (!this.filter.hasOwnProperty('allSelectedYear')) {
            this.filter = { ...this.filter, allSelectedYear: this.validCriteriaTotalYears.length };
          }
        }
      }

      if (this.filter.criteriaSelection === this.financialItemOption) {
        const isBalanceSheetItem = this.getVendorAccountById(this.filter.vendorAccountId).isBalanceSheetItem;
        this.updateBalanceSheetItemRelatedProperties(isBalanceSheetItem, this.filter.isCurrentPlusPriorYearBalanceSheetItem);
      } else if (this.filter.criteriaSelection === this.financialRatioOption) {
        const hasBalanceSheetFinancialItem = this.getRatioById(this.filter.ratioId).hasBalanceSheetFinancialItem;
        this.updateBalanceSheetItemRelatedProperties(hasBalanceSheetFinancialItem, this.filter.isCurrentPlusPriorYearBalanceSheetItem);
      }
    }
  }

  getVendorAccountById(id: number): VendorAccount {
    return _.find(this.filterVendorAccounts, function (va) {
      return va.vendorAccountId === id;
    });
  }

  getRatioById(id: number): Ratio {
    return _.find(this.financialRatios, function (r) {
      return r.ratioId === id;
    });
  }

  get vendorAccounts(): VendorAccount[] {
    return this.filter.criteriaSelection === this.dataAvailabilityOption
      ? _.filter(this.filterVendorAccounts, 'isDataAvalabilityFilter')
      : _.filter(this.filterVendorAccounts, 'isFinancialItemFilter');
  }

  loadInitialData() {
    this.logger.info(this.constructor.name, Constants.LoggerMessages.LOGGER_NGONINIT, Constants.LoggerMessages.LOGGER_START);
    this.filterUtilService.spinnerRS.next(true);

    const filterVendorAccount$ = this.lookupService.getFilterVendorAccount$();
    const allFinancialRatio$ = this.lookupService.getAllFinancialRatio$();

    forkJoin([filterVendorAccount$, allFinancialRatio$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([_vendorAccounts, _financialRatios]) => {
        this.filterVendorAccounts = _vendorAccounts;
        this.financialRatios = _financialRatios.filter((a) => a.availableForFinancialSearchFlag === true);
        this.financialAbsoluteYears = this.filterService.getNumberOfEmployeeAbsoluteYears(
          this.readOnly,
          this.study.periodOfStudyFrom,
          this.study.periodOfStudyTo,
        );

        this.initializeForm();

        this.currencyName = this.study.currency != null ? this.study.currency.currencyName : '';
        this.currencyUnitName = this.study.currencyUnit != null ? this.study.currencyUnit.displayText : '';

        this.filterBefore = JSON.stringify(this.filter);

        this.filterUtilService.spinnerRS.next(false);
      });
  }

  public sortSelection(selection: TreeNode[]): void {
    return this.filterUtilService.sortSelection(selection, true);
  }

  private flattenTree(nodes: TreeNode[]): TreeNode[] {
    return this.filterUtilService.flattenTree(nodes);
  }

  private findNodeRecursive(id: FilterTypes, nodes: TreeNode[]) {
    return this.filterUtilService.findNodeRecursive(id, nodes);
  }

  criteriaChange() {
    if (this.filter instanceof FinancialCriteria) {
      if (this.filter.criteriaSelection === this.dataAvailabilityOption) {
        this.filter.vendorAccount = new VendorAccount();
        this.filter.vendorAccountId = undefined;
        this.filter.allSelectedYear = undefined;
        this.validCriteriaTotalYears = [];
        this.filter.ratio = new Ratio();
        this.filter.ratioId = null;
        this.filter.isPassIfNa = null;
      } else if (this.filter.criteriaSelection === this.financialRatioOption) {
        this.filter.vendorAccount = new VendorAccount();
        this.filter.vendorAccountId = null;
        this.filter.allSelectedYear = undefined;
        this.filter.isPassIfNa = this.searchStrategyDatabases.length > 1;
        this.validCriteriaTotalYears = [];
      } else if (this.filter.criteriaSelection === this.financialItemOption) {
        this.filter.vendorAccount = new VendorAccount();
        this.filter.vendorAccountId = undefined;
        this.filter.allSelectedYear = undefined;
        this.validCriteriaTotalYears = [];
        this.filter.ratio = new Ratio();
        this.filter.ratioId = null;
        this.filter.isPassIfNa = this.searchStrategyDatabases.length > 1;
      }

      this.updateBalanceSheetItemRelatedProperties();
      this.filter.selectedAbsoluteYr = null;
      this.filter.minimumValue = null;
      this.filter.maximumValue = null;
      this.filter.hasAverageForSelectedYears = false;
    }
  }

  ShowValidationMessage(item: NgModel): boolean {
    return (item.errors || false) && (!item.pristine || item.touched);
  }

  updateObjectValue(id: number) {
    this.filterUtilService.spinnerRS.next(true);
    if (this.filter instanceof FinancialCriteria) {
      const criteria = this.filter as FinancialCriteria;

      if (this.filter.criteriaSelection === this.dataAvailabilityOption) {
        criteria.vendorAccount = this.getVendorAccountById(id);
        this.updateBalanceSheetItemRelatedProperties();
      } else if (this.filter.criteriaSelection === this.financialRatioOption) {
        criteria.ratio = this.getRatioById(id);
        this.updateBalanceSheetItemRelatedProperties(criteria.ratio.hasBalanceSheetFinancialItem);
      }
      if (this.filter.criteriaSelection === this.financialItemOption) {
        criteria.vendorAccount = this.getVendorAccountById(id);
        this.updateBalanceSheetItemRelatedProperties(criteria.vendorAccount.isBalanceSheetItem);
      }
    }
    this.filterUtilService.spinnerRS.next(false);
  }

  updateBalanceSheetItemRelatedProperties(isSwitchAvailable = false, cpPlusPyValueSelected = false) {
    this.filter.isCurrentPlusPriorYearBalanceSheetItem = isSwitchAvailable ? cpPlusPyValueSelected : null;
    this.isBalanceSheetValueSwitchAvailable = isSwitchAvailable;
  }

  financialCriteriaTabChange() {
    if (this.filter instanceof FinancialCriteria) {
      this.filter.selectedAbsoluteYr = null;
    }
  }

  public onRowSelectAbsoluteYears(selection: TreeNode[]): void {
    this.sortSelection(selection);

    // if (this.filter instanceof FinancialCriteria) {
    const criteria = this.filter as FinancialCriteria;
    const validCriteriaTotalYears = [];
    criteria.allSelectedYear = undefined;
    selection.forEach((ele, index) => {
      validCriteriaTotalYears.push(index + 1);
    });
    this.validCriteriaTotalYears = validCriteriaTotalYears;
    if (
      this.filter.hasAverageForSelectedYears &&
      (this.financialRatioOption === this.filter.criteriaSelection || this.financialItemOption === this.filter.criteriaSelection)
    ) {
      criteria.allSelectedYear = this.validCriteriaTotalYears.length;
    }
    // }
  }

  public onRowUnselectAbsoluteYears(): void {
    // if (this.filter instanceof FinancialCriteria) {
    const criteria = this.filter as FinancialCriteria;
    const validCriteriaTotalYears = [];
    criteria.allSelectedYear = undefined;
    criteria.selectedAbsoluteYr.forEach((ele, index) => {
      validCriteriaTotalYears.push(index + 1);
    });
    this.validCriteriaTotalYears = validCriteriaTotalYears;
    if (
      this.filter.hasAverageForSelectedYears &&
      (this.financialRatioOption === this.filter.criteriaSelection || this.financialItemOption === this.filter.criteriaSelection)
    ) {
      criteria.allSelectedYear = this.validCriteriaTotalYears.length;
    }
    // }
  }

  public onAverageChange(): void {
    this.formDirty = true;
    if (this.filter instanceof FinancialCriteria) {
      const criteria = this.filter as FinancialCriteria;
      if (
        this.filter.hasAverageForSelectedYears &&
        (this.financialRatioOption === this.filter.criteriaSelection || this.financialItemOption === this.filter.criteriaSelection)
      ) {
        criteria.allSelectedYear = this.validCriteriaTotalYears.length;
      } else {
        criteria.allSelectedYear = undefined;
      }
    }
  }

  checkEndGreaterThanBeginning(beginning: string, ending: string): boolean {
    return this.filterUtilService.checkEndGreaterThanBeginning(beginning, ending);
  }

  private setPassIfNaOption(): void {
    if (this.searchStrategyDatabases.length > 1) {
      this.disablePassIfNa = true;
      this.filter.isPassIfNa = true;
    } else if (!this.filter.isPassIfNa) {
      this.filter.isPassIfNa = false;
    }
  }
}
